'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  Input,
  MessagePlugin,
  Steps,
  Tooltip,
} from 'tdesign-react';
import {
  CallIcon,
  ChevronLeftIcon,
  CloseIcon,
  ErrorCircleFilledIcon,
  InfoCircleFilledIcon,
  MailIcon,
  PoweroffIcon,
} from 'tdesign-icons-react';
import { getClientUserInfoApi } from '@ssv-website/hooks';
import styles from './AccountDeletionFlow.module.css';

const { StepItem } = Steps;

type AccountDeletionLocale = 'zh' | 'en';
type AccountDeletionMode = 'desktop' | 'mobile';
type DeletionStage = 'intro' | 'flow';

interface TencentCaptchaResult {
  ret?: number;
  ticket?: string;
  randstr?: string;
}

interface CodeverifyAppSendResponse {
  code: number;
  data?: {
    codeTTL?: number;
  };
  message?: string;
  msg?: string;
}

interface AccountPhoneInfo {
  displayPhone: string;
  verifyPhone: string;
}

interface AccountEmailInfo {
  displayEmail: string;
  verifyEmail: string;
}

interface AccountDeletionSubmitResp {
  success?: boolean;
}

interface AccountDeletionSubmitResult {
  code?: number;
  msg?: string;
  data?: AccountDeletionSubmitResp;
}

interface AccountDeletionFlowProps {
  locale?: AccountDeletionLocale;
  mode?: AccountDeletionMode;
  accountEmail?: string;
  refreshAccountEmail?: () => Promise<AccountEmailInfo>;
  open: boolean;
  onClose: () => void;
}

interface AgreementTextSegment {
  text: string;
  bold?: boolean;
}

type AgreementParagraph = readonly AgreementTextSegment[];

const STEP_COUNT = 3;
const CORAL_APP_ID = process.env.NEXT_PUBLIC_CORAL_APPID || 'SSVOPEN';
const CORAL_CAPTCHA_APP_ID = '193404462';
const ACCOUNT_DELETION_CAPTCHA_APP_ID = '190872752';
const CORAL_PHONE_CODE = '86';
const CODEVERIFY_APP_SEND_API = '/api/codeverify/app/send';
const OVERSEA_ACCOUNT_DELETION_SEND_CODE_API =
  '/portalWebAPI/oversea/account/deletion/code/send';
const OVERSEA_ACCOUNT_DELETION_VERIFY_CODE_API =
  '/portalWebAPI/oversea/account/deletion/code/verify';
const ACCOUNT_DELETION_SUBMIT_API: Record<AccountDeletionLocale, string> = {
  zh: '/openTechPortalWebAPI/techforgood/account/deletion/submit',
  en: '/portalWebAPI/oversea/account/deletion/submit',
};
const VERIFICATION_CODE_COUNTDOWN = 60;
const ZH_MOBILE_CONFIRM_HINT =
  '您即将注销 SSV 账号，无冷静期、申请提交后不可撤销。注销将清空账号全部数据、解绑实名信息、终止所有平台权益，永久无法恢复，请谨慎操作。';
const ZH_MOBILE_CONFIRM_EMPHASIS = '请确认您已了解以上风险，自愿申请注销账号。';
const EN_MOBILE_CONFIRM_HINT =
  'You are about to delete your primary SSV account. This action is immediate and cannot be undone.\nDeleting your account will erase all data, unlink your identity information, and permanently terminate all platform benefits. Please proceed with caution.';
const EN_MOBILE_CONFIRM_EMPHASIS =
  'I understand the risks above and want to delete my account.';
const EN_MOBILE_RISK_AGREE =
  'I have read and agree to the “SSV Account Cancellation Notice”';
const EN_FINAL_SUBMIT = 'Confirm Deletion';
const EN_RISK_PARAGRAPHS: readonly AgreementParagraph[] = [
  [{ text: 'Dear User,' }],
  [
    {
      text: 'Before submitting a request to cancel your SSV account, please read this Account Cancellation Notice carefully. By clicking ',
    },
    { text: '“Confirm Cancellation Request”', bold: true },
    {
      text: ', you acknowledge that you have read and understood this Notice and voluntarily agree to be bound by its terms.',
    },
  ],
  [{ text: 'Article 1 Definitions and Scope', bold: true }],
  [
    { text: '1.1', bold: true },
    {
      text: ' For the purposes of this Notice, an “SSV Account” refers to the primary account that you have registered on the SSV platform and, where applicable, completed identity verification for. It includes all personal information, identity verification information, business data, platform rights and benefits, service permissions, linked accounts or services, and other information or interests associated with the account.',
    },
  ],
  [
    { text: '1.2', bold: true },
    {
      text: ' This Notice applies only to the cancellation of a primary account on the SSV platform. Once your account has been successfully cancelled, the user service relationship between you and the SSV platform will be permanently terminated.',
    },
  ],
  [
    { text: '1.3', bold: true },
    {
      text: ' This Notice supplements the SSV Platform User Service Agreement and the Privacy Policy. Any matters not addressed in this Notice will be governed by the applicable platform rules and applicable laws and regulations.',
    },
  ],
  [{ text: 'Article 2 Key Account Cancellation Rules', bold: true }],
  [{ text: 'No Cooling-off Period; Cancellation Is Irreversible', bold: true }],
  [
    { text: '2.1 No Cooling-off Period: ', bold: true },
    {
      text: 'There is no cooling-off period for account cancellation. Once you submit a cancellation request, the request will be immediately processed and cannot be revoked, withdrawn, or terminated.',
    },
  ],
  [
    { text: '2.2 Effect of the Cancellation Request:', bold: true },
    {
      text: ' If your cancellation request is approved, your account will be permanently cancelled with immediate effect, and the cancellation cannot be reversed. If your request is rejected, the cancellation process will automatically end, and your account will remain available for normal use.',
    },
  ],
  [{ text: 'Article 3 Requirements for Account Cancellation', bold: true }],
  [
    {
      text: 'By submitting a cancellation request, you confirm that your SSV Account meets all of the following requirements. The platform may conduct automated checks and manual reviews to verify your eligibility. Any request that does not meet these requirements may be rejected:',
    },
  ],
  [
    { text: '3.1', bold: true },
    {
      text: ' The account is in good standing and is not subject to any suspension, restriction, abnormal status, penalty, appeal, dispute, or other unresolved matter.',
    },
  ],
  [
    { text: '3.2', bold: true },
    {
      text: ' The account has no incomplete orders, outstanding payments or funds, unused or unclaimed rights and benefits, pending invoices, or services awaiting completion.',
    },
  ],
];

declare global {
  interface Window {
    TencentCaptcha?: new (
      appId: string,
      callback: (result: TencentCaptchaResult) => void,
      options?: Record<string, unknown>
    ) => {
      show: () => void;
    };
  }
}

let tencentCaptchaLoader: Promise<void> | null = null;

const formatPhoneNumber = (phone?: string) => {
  const value = phone?.trim();
  if (!value) {
    return '--';
  }

  const normalized = value.replace(/\s/g, '').replace(/^(\+?86)/, '');
  if (/^1\d{10}$/.test(normalized)) {
    return `${normalized.slice(0, 3)} ${normalized.slice(
      3,
      7
    )} ${normalized.slice(7)}`;
  }

  return value;
};

const normalizePhoneNumber = (phone?: string) => {
  const value = phone?.trim();
  if (!value) {
    return '';
  }

  return value.replace(/\s/g, '').replace(/^(\+?86)/, '');
};

const isValidEmail = (email: string) => /\S+@\S+\.\S+/.test(email.trim());
const normalizeAccountEmailInfo = (
  value: AccountEmailInfo | undefined,
  fallbackEmail = ''
): AccountEmailInfo => {
  const displayEmail = (value?.displayEmail || fallbackEmail).trim();
  const verifyEmail = (value?.verifyEmail || '').trim();

  return {
    displayEmail,
    verifyEmail,
  };
};

const loadTencentCaptcha = () => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('当前环境不支持验证码校验'));
  }

  if (window.TencentCaptcha) {
    return Promise.resolve();
  }

  if (tencentCaptchaLoader) {
    return tencentCaptchaLoader;
  }

  tencentCaptchaLoader = new Promise((resolve, reject) => {
    const scriptId = 'tencent-cloud-captcha';
    const existingScript = document.getElementById(scriptId);
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener(
        'error',
        () => reject(new Error('验证码脚本加载失败')),
        { once: true }
      );
      return;
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.src = 'https://turing.captcha.qcloud.com/TCaptcha.js';
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('验证码脚本加载失败'));
    document.body.appendChild(script);
  });

  return tencentCaptchaLoader;
};

const getCaptchaConfigMissingMessage = (locale: AccountDeletionLocale) =>
  locale === 'en'
    ? 'Captcha configuration is missing.'
    : '验证码配置缺失，请稍后重试';

const showTencentCaptcha = async (
  locale: AccountDeletionLocale,
  captchaAppId: string
) => {
  await loadTencentCaptcha();

  return new Promise<TencentCaptchaResult>((resolve, reject) => {
    const Captcha = window.TencentCaptcha;
    if (!Captcha) {
      reject(new Error('验证码能力初始化失败'));
      return;
    }
    if (!captchaAppId) {
      reject(new Error(getCaptchaConfigMissingMessage(locale)));
      return;
    }

    // 英文站显式传 userLanguage，避免腾讯防水墙回落到中文默认文案。
    new Captcha(
      captchaAppId,
      (result) => {
        if (result.ticket && result.randstr) {
          resolve(result);
          return;
        }

        if (result.ret === 2) {
          reject(new Error('已取消验证码校验'));
          return;
        }

        reject(
          new Error(
            locale === 'en'
              ? 'Verification failed. Please try again.'
              : '验证码校验失败，请重试'
          )
        );
      },
      locale === 'en'
        ? { enableAged: true, userLanguage: 'en' }
        : { enableAged: true }
    ).show();
  });
};

const requestCoralPhoneCode = async (
  phone: string,
  captcha: TencentCaptchaResult
) => {
  const response = await fetch(CODEVERIFY_APP_SEND_API, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      business: CORAL_APP_ID,
      phone: {
        code: CORAL_PHONE_CODE,
        number: phone,
      },
      rand_str: captcha.randstr,
      ticket: captcha.ticket,
    }),
  });

  const result = (await response
    .json()
    .catch(() => null)) as CodeverifyAppSendResponse | null;
  if (!response.ok || !result || result.code !== 0) {
    throw new Error(result?.message || result?.msg || '验证码发送失败');
  }

  return result.data;
};

const requestOverseaAccountDeletionEmailCode = async (
  email: string,
  captcha: TencentCaptchaResult
) => {
  const response = await fetch(OVERSEA_ACCOUNT_DELETION_SEND_CODE_API, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      email,
      ticket: captcha.ticket,
      randstr: captcha.randstr,
    }),
  });

  const result = (await response
    .json()
    .catch(() => null)) as CodeverifyAppSendResponse | null;
  if (!response.ok || !result || result.code !== 0) {
    throw new Error(result?.message || result?.msg || 'Failed to send code');
  }

  return result.data;
};

const requestOverseaAccountDeletionVerifyCode = async (
  verificationCode: string
) => {
  const response = await fetch(OVERSEA_ACCOUNT_DELETION_VERIFY_CODE_API, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      verificationCode,
    }),
  });

  const result = (await response
    .json()
    .catch(() => null)) as AccountDeletionSubmitResult | null;
  if (!response.ok || !result || result.code !== 0) {
    throw new Error(result?.msg || 'Verification failed. Please try again.');
  }
  if (result.data?.success === false) {
    throw new Error(result.msg || 'Verification failed. Please try again.');
  }
};

let pageScrollLockCount = 0;
let pageScrollLockSnapshot: {
  bodyOverflow: string;
  bodyPaddingRight: string;
  htmlOverflow: string;
} | null = null;

const lockPageScroll = () => {
  if (typeof document === 'undefined') {
    return () => undefined;
  }

  if (pageScrollLockCount === 0) {
    const { body, documentElement } = document;
    const scrollbarWidth = window.innerWidth - documentElement.clientWidth;

    pageScrollLockSnapshot = {
      bodyOverflow: body.style.overflow,
      bodyPaddingRight: body.style.paddingRight,
      htmlOverflow: documentElement.style.overflow,
    };

    // 弹窗打开时锁住页面滚动，避免鼠标在遮罩层滚动时带动外层页面。
    documentElement.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${scrollbarWidth}px`;
    }
  }

  pageScrollLockCount += 1;

  return () => {
    pageScrollLockCount = Math.max(0, pageScrollLockCount - 1);
    if (pageScrollLockCount !== 0 || !pageScrollLockSnapshot) {
      return;
    }

    document.documentElement.style.overflow =
      pageScrollLockSnapshot.htmlOverflow;
    document.body.style.overflow = pageScrollLockSnapshot.bodyOverflow;
    document.body.style.paddingRight = pageScrollLockSnapshot.bodyPaddingRight;
    pageScrollLockSnapshot = null;
  };
};

const COPY = {
  zh: {
    title: '账号信息注销',
    steps: ['风险需知', '验证账号', '注销账号'],
    riskTitle: '账号注销风险需知',
    riskAgree: '我已阅读并同意“账号注销风险需知”',
    riskReadTip: '请先完整阅读账号注销风险须知',
    next: '下一步',
    cancel: '取消',
    previous: '上一步',
    submit: '确认注销',
    verificationTab: '手机号验证',
    verificationPlaceholder: '请输入验证码',
    sendCode: '获取验证码',
    resendCode: '剩余{seconds}s',
    invalidPhone: '当前账号未绑定有效手机号',
    codeSent: '验证码已发送',
    submitFailed: '注销失败，请稍后重试',
    introTitle: '确定要注销账号信息吗？',
    introApply: '申请注销',
    introText:
      '您即将注销 SSV 主账号，无冷静期、申请提交后不可撤销。\n注销将清空账号全部数据、解绑实名信息、终止所有平台权益，永久无法恢复，请谨慎操作。',
    introConfirmText: '请确认您已了解以上风险，自愿申请注销账号。',
    confirmTitle: '',
    confirmHint:
      '您即将注销 SSV 主账号，无冷静期、申请提交后不可撤销。\n注销将清空账号全部数据、解绑实名信息、终止所有平台权益，永久无法恢复，请谨慎操作。',
    confirmEmphasis: '请确认您已了解以上风险，自愿申请注销本次SSV账号。',
    verificationHint: '验证码错误过多时将按现有验证码能力提示稍后重试。',
    riskText:
      '尊敬的用户：\n在你提交 SSV 账号注销申请前，请认真阅读本协议全部条款。当你点击「确认注销申请」，即表示你已充分理解、自愿同意本协议所有内容，本协议即时生效，并对你产生法律约束力。\n第一条 定义与适用范围\n1.1 本协议所称SSV 账号，指你在 SSV 平台注册并完成实名认证的平台主账号，包含账号关联的个人信息、实名资料、业务数据、平台权益、服务权限、绑定关系等全部内容。\n1.2 本协议仅适用于 SSV 平台主账号注销操作，账号注销后，你与 SSV 平台之间的用户服务关系全面终止。\n1.3 本协议为《SSV 平台用户服务协议》《隐私政策》的补充协议，未尽事宜均遵照平台现有规则及国家法律法规执行。\n\n第二条 注销核心规则（无冷静期、不可逆）\n2.1 无冷静期约定：本次账号注销流程不设置任何冷静期。你一旦提交注销申请，申请立即锁定，不可撤销、无法撤回、不得终止。\n2.2 生效规则：注销申请审核通过后，账号立即完成永久注销，注销结果不可逆；申请被驳回的，本次注销流程自动终止，账号恢复正常使用状态。\n\n第三条 账号注销前置条件\n你承诺提交注销申请时，你的 SSV 账号必须同时满足以下全部条件，平台有权进行系统核验与人工复核，不符合条件将直接驳回申请：\n3.1 账号状态正常，无封禁、限制、异常、处罚、申诉、纠纷等未办结记录；\n3.2 账号内无未完成订单、待结算资金、未核销权益、待处理票据、待履约服务等各类未完结业务；\n3.3 账号无绑定团队、组织、项目、第三方授权、子账号等关联关系，或已提前完成解绑、移交、注销；\n3.4 你已自行备份账号内需要留存的资料、文件、数据，平台不承担注销后数据找回义务；\n3.5 你已明确知晓并自愿放弃账号内全部平台权益、积分、权限、专属服务等内容。\n第四条 账号注销生效后的后果\n账号经平台审核通过并完成注销后，将产生以下永久不可逆结果，你自愿完全接受：\n4.1 服务终止：你将永久丧失该 SSV 账号的使用权限，无法登录、使用 SSV 平台任何功能、服务及产品，账号不再支持找回、恢复、补办。\n4.2 实名与绑定处理：账号对应的实名认证信息将按《隐私政策》进行解绑与管理；账号绑定的手机号、邮箱、第三方账号等关联关系全部解除。\n4.3 数据处理：账号内产生的个人资料、业务记录、上传文件、聊天记录、操作日志等用户数据，除法律法规要求必须留存的合规记录外，其余数据将进行删除或匿名化处理，所有数据均无法恢复。\n4.4 权益作废：账号内所有积分、福利、会员权益、专属权限、兑换凭证等全部立即作废，不予补发、折现、转移。\n4.5 账号回收：注销完成后，平台有权对该账号名称、账号标识等进行回收、重新分配，你无权主张任何权利。\n第五条 用户权利与承诺\n5.1 你承诺：本次注销操作系本人自主、自愿发起，为本人真实意愿，不存在被盗号、误操作、被胁迫等情形，自愿承担注销操作带来的一切后果。\n5.2 你承诺：已全额结清账号项下所有费用、债务、履约事项，无任何未了结争议、索赔、纠纷，账号注销后不再就原账号相关内容向平台提出任何诉求。\n5.3 你承诺：已充分阅读并理解本协议、平台用户服务协议及隐私政策，知晓账号注销的全部风险与后果。\n5.4 你保证提交的注销原因及相关信息真实有效，不得恶意、批量、反复提交注销申请，不得利用注销流程扰乱平台正常秩序。\n第六条 平台权利与数据合规\n6.1 平台有权对注销申请进行业务核验、安全核查、人工审核，对存在风险、不符合注销条件、恶意申请的账号，有权直接驳回申请，且无需向你承担任何责任。\n6.2 依据《中华人民共和国网络安全法》《中华人民共和国个人信息保护法》等法律法规要求，平台有权留存账号操作日志、注销审核记录、合规凭证等必要数据，留存周期遵照国家规定执行，该部分数据不予删除。\n6.3 平台将严格按照隐私政策保护你的个人信息，不会违规泄露、出售、非法使用合规留存数据。\n6.4 因系统维护、网络异常、第三方故障等非平台主观原因导致的短暂流程延迟，平台不承担额外责任。\n第七条 免责条款\n7.1 因你未仔细阅读本协议、自身误操作、主动提交注销申请所造成的账号丢失、数据删除、权益损失等后果，均由你自行承担，平台不承担恢复、赔偿责任。\n7.2 因账号存在未完结业务、违规记录、异常状态导致注销申请被驳回的，平台无需承担任何解释及补偿责任。\n7.3 账号注销生效后，平台不再为该账号提供任何技术支持、数据恢复、权益补发等服务。\n第八条 协议生效与附则\n8.1 本协议自你点击「提交注销申请」之日起正式生效。\n8.2 本协议内容若与国家现行法律法规相冲突，以法律法规为准。\n8.3 若你在提交申请前对注销规则、协议条款存在疑问，请提前联系平台客服咨询；申请提交后，平台不再受理针对本次注销流程的异议。',
  },
  en: {
    title: 'Delete Account Information',
    steps: ['Important Notice', 'Verify Account', 'Delete Account'],
    riskTitle: 'SSV Account Cancellation Notice',
    riskAgree: 'I have read and agree to the SSV Account Cancellation Notice.',
    riskReadTip: 'Please read the SSV account cancellation notice first.',
    next: 'Next',
    cancel: 'Cancel',
    previous: 'Back',
    submit: 'Delete Account',
    verificationTab: 'Verify Email',
    verificationPlaceholder: 'Enter verification code',
    sendCode: 'Send Code',
    resendCode: '{seconds}s',
    invalidPhone: 'No valid phone number is bound to this account.',
    codeSent: 'Verification code sent.',
    submitFailed: 'Deletion failed. Please try again later.',
    introTitle: 'Are you sure you want to delete your account?',
    introApply: 'Delete My Account',
    introText:
      'You are about to delete your primary SSV account. This action is immediate and cannot be undone.\nDeleting your account will erase all data, unlink your identity information, and permanently terminate all platform benefits. Please proceed with caution.',
    introConfirmText:
      'I understand the risks above and want to delete my account.',
    confirmTitle: '',
    confirmHint:
      'You are about to delete your primary SSV account. This action is immediate and cannot be undone.\nDeleting your account will erase all data, unlink your identity information, and permanently terminate all platform benefits. Please proceed with caution.',
    confirmEmphasis:
      'I understand the risks above and want to delete my account.',
    verificationHint:
      'Too many incorrect attempts will be handled by the existing verification capability.',
  },
} as const;

export const AccountDeletionFlow = ({
  locale = 'zh',
  mode = 'desktop',
  accountEmail = '',
  refreshAccountEmail,
  open,
  onClose,
}: AccountDeletionFlowProps) => {
  const text = COPY[locale];
  const [current, setCurrent] = useState(0);
  const [riskAccepted, setRiskAccepted] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [verifiedOverseaCode, setVerifiedOverseaCode] = useState('');
  const [riskReadComplete, setRiskReadComplete] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [codeCountdown, setCodeCountdown] = useState(0);
  const [accountDisplayPhone, setAccountDisplayPhone] = useState('');
  const [accountVerifyPhone, setAccountVerifyPhone] = useState('');
  const [accountDisplayEmail, setAccountDisplayEmail] = useState('');
  const [accountVerifyEmail, setAccountVerifyEmail] = useState('');
  const [accountInfoLoading, setAccountInfoLoading] = useState(false);
  const [desktopStage, setDesktopStage] = useState<DeletionStage>('flow');
  const [mounted, setMounted] = useState(false);
  const riskContentRef = useRef<HTMLDivElement>(null);
  const mobileScrollRef = useRef<HTMLElement>(null);
  const accountInfoRequestRef = useRef<Promise<AccountPhoneInfo> | null>(null);
  const needsIntro = mode === 'desktop' || mode === 'mobile';
  const normalizedAccountPhone = normalizePhoneNumber(accountVerifyPhone);
  const formattedAccountPhone = formatPhoneNumber(accountDisplayPhone);
  const formattedAccountEmail =
    accountDisplayEmail.trim() || accountEmail.trim() || '--';
  const isChineseMobileFlow = mode === 'mobile' && locale === 'zh';
  const isEnglishMobileFlow = mode === 'mobile' && locale === 'en';

  const refreshAccountPhoneInfo = useCallback(async () => {
    if (accountInfoRequestRef.current) {
      return accountInfoRequestRef.current;
    }

    setAccountInfoLoading(true);
    const request = getClientUserInfoApi()
      .then(({ data }) => {
        const displayPhone = data?.info?.Phone || data?.info?.Mobile || '';
        const verifyPhone = data?.info?.Mobile || '';
        setAccountDisplayPhone(displayPhone);
        setAccountVerifyPhone(verifyPhone);
        return { displayPhone, verifyPhone };
      })
      .catch((error) => {
        setAccountDisplayPhone('');
        setAccountVerifyPhone('');
        throw error;
      })
      .finally(() => {
        setAccountInfoLoading(false);
        accountInfoRequestRef.current = null;
      });

    accountInfoRequestRef.current = request;
    return request;
  }, []);

  const checkRiskReadComplete = useCallback(() => {
    const target =
      mode === 'mobile' ? mobileScrollRef.current : riskContentRef.current;
    if (!target) {
      return;
    }

    const distanceToBottom =
      target.scrollHeight - target.scrollTop - target.clientHeight;
    if (distanceToBottom <= 2) {
      setRiskReadComplete(true);
    }
  }, [mode]);

  useEffect(() => {
    // 避免 Dialog/Portal 在 SSR 与客户端首屏结构不一致导致 hydration mismatch。
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }
    setCurrent(0);
    setRiskAccepted(false);
    setRiskReadComplete(false);
    setVerificationCode('');
    setSubmitting(false);
    setVerifyingCode(false);
    setVerifiedOverseaCode('');
    setSendingCode(false);
    setCodeCountdown(0);
    setAccountDisplayPhone('');
    setAccountVerifyPhone('');
    setAccountDisplayEmail(accountEmail.trim());
    setAccountVerifyEmail('');
    setDesktopStage(needsIntro ? 'intro' : 'flow');
    if (locale === 'zh') {
      void refreshAccountPhoneInfo().catch(() => undefined);
    } else if (refreshAccountEmail) {
      void refreshAccountEmail()
        .then((emailInfo) => {
          const { displayEmail, verifyEmail } = normalizeAccountEmailInfo(
            emailInfo,
            accountEmail
          );
          setAccountDisplayEmail(displayEmail);
          setAccountVerifyEmail(verifyEmail);
        })
        .catch(() => undefined);
    }
  }, [
    accountEmail,
    locale,
    needsIntro,
    open,
    refreshAccountEmail,
    refreshAccountPhoneInfo,
  ]);

  useEffect(() => {
    if (!mounted || !open) {
      return;
    }
    return lockPageScroll();
  }, [mounted, open]);

  useEffect(() => {
    if (!mounted || !open || desktopStage !== 'flow' || current !== 0) {
      return;
    }

    const frame = window.requestAnimationFrame(checkRiskReadComplete);
    return () => window.cancelAnimationFrame(frame);
  }, [checkRiskReadComplete, current, desktopStage, mounted, open]);

  useEffect(() => {
    if (!open || codeCountdown <= 0) {
      return;
    }

    const timer = window.setTimeout(() => {
      setCodeCountdown((value) => Math.max(value - 1, 0));
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [codeCountdown, open]);

  const canNext = useMemo(() => {
    if (current === 0) return riskAccepted;
    if (current === 1) return verificationCode.trim().length > 0;
    return true;
  }, [current, riskAccepted, verificationCode]);

  if (!mounted || !open) {
    return null;
  }

  const sendVerificationCode = async () => {
    if (sendingCode || codeCountdown > 0) {
      return;
    }

    if (locale === 'en') {
      setSendingCode(true);
      try {
        const emailInfo = refreshAccountEmail
          ? normalizeAccountEmailInfo(await refreshAccountEmail(), accountEmail)
          : normalizeAccountEmailInfo({
              displayEmail: formattedAccountEmail,
              verifyEmail: accountVerifyEmail,
            });

        setAccountDisplayEmail(emailInfo.displayEmail);
        setAccountVerifyEmail(emailInfo.verifyEmail);

        if (!isValidEmail(emailInfo.verifyEmail)) {
          void MessagePlugin.warning(
            'No valid email address is bound to this account.'
          );
          return;
        }
        setVerifiedOverseaCode('');
        const captcha = await showTencentCaptcha(
          locale,
          ACCOUNT_DELETION_CAPTCHA_APP_ID
        );
        await requestOverseaAccountDeletionEmailCode(
          emailInfo.verifyEmail,
          captcha
        );
        setCodeCountdown(VERIFICATION_CODE_COUNTDOWN);
        void MessagePlugin.success(text.codeSent);
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : 'Failed to send code. Please try again.';
        if (message !== '已取消验证码校验') {
          void MessagePlugin.error(message);
        }
      } finally {
        setSendingCode(false);
      }
      return;
    }

    let verifiedPhone = normalizedAccountPhone;
    if (!/^1\d{10}$/.test(verifiedPhone)) {
      try {
        const { verifyPhone } = await refreshAccountPhoneInfo();
        verifiedPhone = normalizePhoneNumber(verifyPhone);
      } catch {
        void MessagePlugin.error('账号信息获取失败，请稍后重试');
        return;
      }
    }

    if (!/^1\d{10}$/.test(verifiedPhone)) {
      void MessagePlugin.warning(text.invalidPhone);
      return;
    }

    setSendingCode(true);
    try {
      const captcha = await showTencentCaptcha(locale, CORAL_CAPTCHA_APP_ID);
      await requestCoralPhoneCode(verifiedPhone, captcha);
      setCodeCountdown(VERIFICATION_CODE_COUNTDOWN);
      void MessagePlugin.success(text.codeSent);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : '验证码发送失败，请重试';
      if (message !== '已取消验证码校验') {
        void MessagePlugin.error(message);
      }
    } finally {
      setSendingCode(false);
    }
  };

  const submitDeletion = async () => {
    setSubmitting(true);
    try {
      const payload =
        locale === 'en'
          ? { riskConfirmed: true }
          : {
              verificationCode: verificationCode.trim(),
              riskConfirmed: true,
            };
      const response = await fetch(ACCOUNT_DELETION_SUBMIT_API[locale], {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const result = (await response
        .json()
        .catch(() => null)) as AccountDeletionSubmitResult | null;
      if (!response.ok || !result || result.code !== 0) {
        throw new Error(result?.msg || text.submitFailed);
      }
      if (result.data?.success === false) {
        throw new Error(result.msg || text.submitFailed);
      }
      window.location.href = '/';
    } catch (error) {
      const message =
        error instanceof Error ? error.message : text.submitFailed;
      void MessagePlugin.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const goNext = async () => {
    if (current === 0 && !riskAccepted) {
      void MessagePlugin.warning(text.riskReadTip);
      return;
    }

    if (current === 1 && locale === 'en') {
      const currentCode = verificationCode.trim();
      if (verifiedOverseaCode !== currentCode) {
        setVerifyingCode(true);
        try {
          await requestOverseaAccountDeletionVerifyCode(currentCode);
          setVerifiedOverseaCode(currentCode);
        } catch (error) {
          const message =
            error instanceof Error
              ? error.message
              : 'Verification failed. Please try again.';
          void MessagePlugin.error(message);
          return;
        } finally {
          setVerifyingCode(false);
        }
      }
    }

    if (current < STEP_COUNT - 1) {
      setCurrent((value) => value + 1);
      return;
    }
    void submitDeletion();
  };

  const goPrevious = () => {
    if (current === 0) {
      onClose();
      return;
    }
    setCurrent((value) => value - 1);
  };

  const applyDeletion = () => {
    setDesktopStage('flow');
  };

  const renderIntroDialog = () => (
    <Dialog
      className={styles.introDialog}
      dialogClassName={styles.introDialogCard}
      visible={open}
      width={650}
      placement="center"
      closeBtn={false}
      closeOnEscKeydown={false}
      closeOnOverlayClick={false}
      footer={false}
      header={false}
      onClose={onClose}
    >
      <div className={styles.introShell}>
        <header className={styles.introHeader}>
          <div className={styles.introTitleWrap}>
            <ErrorCircleFilledIcon
              className={styles.introWarningIcon}
              size={24}
            />
            <h2 className={styles.introTitle}>{text.introTitle}</h2>
          </div>
          <button
            type="button"
            className={styles.introCloseButton}
            aria-label="close"
            onClick={onClose}
          >
            <CloseIcon size={16} />
          </button>
        </header>
        <div className={styles.introContent}>
          <p className={styles.introSecondaryText}>{text.introText}</p>
          <p className={styles.introPrimaryText}>{text.introConfirmText}</p>
        </div>
        <footer className={styles.introFooter}>
          <Button
            className={styles.introCancelButton}
            size="medium"
            theme="default"
            variant="base"
            onClick={onClose}
          >
            {text.cancel}
          </Button>
          <Button
            className={styles.introApplyButton}
            size="medium"
            theme="primary"
            onClick={applyDeletion}
          >
            {text.introApply}
          </Button>
        </footer>
      </div>
    </Dialog>
  );

  const renderMobileIntroDialog = () => (
    <div
      className={`${styles.mobileIntroOverlay} ${
        isEnglishMobileFlow ? styles.mobileIntroOverlayEn : ''
      }`.trim()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="account-deletion-mobile-intro-title"
    >
      <section className={styles.mobileIntroDialog}>
        <div className={styles.mobileIntroContent}>
          <h2
            id="account-deletion-mobile-intro-title"
            className={styles.mobileIntroTitle}
          >
            {text.introTitle}
          </h2>
          <div
            className={`${styles.mobileIntroDescription} ${
              isEnglishMobileFlow ? styles.mobileIntroDescriptionEn : ''
            }`.trim()}
          >
            {locale === 'en' ? (
              <>
                <p className={styles.mobileIntroSecondaryText}>
                  {text.introText.replace(/\n/g, '')}
                </p>
                <p className={styles.mobileIntroPrimaryText}>
                  {text.introConfirmText}
                </p>
              </>
            ) : (
              <>
                <p className={styles.mobileIntroSecondaryText}>
                  {text.introText.replace(/\n/g, '')}
                </p>
                <p className={styles.mobileIntroPrimaryText}>
                  {text.introConfirmText}
                </p>
              </>
            )}
          </div>
        </div>
        <footer
          className={`${styles.mobileIntroFooter} ${
            isEnglishMobileFlow ? styles.mobileIntroFooterEn : ''
          }`.trim()}
        >
          <Button
            className={styles.mobileIntroCancelButton}
            size="medium"
            theme="default"
            variant="base"
            onClick={onClose}
          >
            {text.cancel}
          </Button>
          <Button
            className={styles.mobileIntroApplyButton}
            size="medium"
            theme="primary"
            onClick={applyDeletion}
          >
            {text.introApply}
          </Button>
        </footer>
      </section>
    </div>
  );

  const renderSteps = (className?: string) => (
    <Steps className={className} current={current} readonly>
      {text.steps.map((item) => (
        <StepItem key={item} title={item} />
      ))}
    </Steps>
  );

  const renderEnglishRiskText = () =>
    EN_RISK_PARAGRAPHS.map((paragraph, paragraphIndex) => (
      <p className={styles.riskParagraph} key={paragraphIndex}>
        {paragraph.map((segment, segmentIndex) =>
          segment.bold ? (
            <strong key={segmentIndex}>{segment.text}</strong>
          ) : (
            segment.text
          )
        )}
      </p>
    ));

  const renderRisk = () => (
    <section className={styles.panel}>
      <h2 className={styles.riskTitle}>{text.riskTitle}</h2>
      <div
        ref={riskContentRef}
        className={
          mode === 'mobile' ? styles.mobileRiskContent : styles.riskContent
        }
        onScroll={checkRiskReadComplete}
      >
        {locale === 'en' ? renderEnglishRiskText() : COPY.zh.riskText}
      </div>
    </section>
  );

  const renderVerification = () => (
    <section
      className={`${styles.verifyPanel} ${
        isEnglishMobileFlow ? styles.verifyPanelEn : ''
      }`.trim()}
    >
      <div className={styles.verifyTab}>
        <div className={styles.verifyTabContent}>
          {locale === 'en' ? (
            <MailIcon className={styles.verifyTabIcon} size={16} />
          ) : (
            <CallIcon className={styles.verifyTabIcon} size={16} />
          )}
          {text.verificationTab}
        </div>
      </div>

      <div className={styles.verifyInputs}>
        <div className={styles.phoneDisplayInput}>
          <span className={styles.phoneNumberText}>
            {locale === 'en' ? formattedAccountEmail : formattedAccountPhone}
          </span>
        </div>

        <div className={styles.verifyCodeRow}>
          <Input
            className={styles.verifyCodeInput}
            size="large"
            value={verificationCode}
            placeholder={text.verificationPlaceholder}
            onChange={(value) => {
              setVerificationCode(String(value));
              setVerifiedOverseaCode('');
            }}
          />
          <Button
            className={styles.sendCodeButton}
            size="large"
            variant="outline"
            disabled={accountInfoLoading || sendingCode || codeCountdown > 0}
            loading={sendingCode}
            onClick={sendVerificationCode}
          >
            {codeCountdown > 0
              ? text.resendCode.replace('{seconds}', String(codeCountdown))
              : text.sendCode}
          </Button>
        </div>
      </div>
    </section>
  );

  const renderConfirm = () => {
    let confirmHint: string = text.confirmHint;
    let confirmEmphasis: string = text.confirmEmphasis;

    if (isChineseMobileFlow) {
      confirmHint = ZH_MOBILE_CONFIRM_HINT;
      confirmEmphasis = ZH_MOBILE_CONFIRM_EMPHASIS;
    }
    if (isEnglishMobileFlow) {
      confirmHint = EN_MOBILE_CONFIRM_HINT;
      confirmEmphasis = EN_MOBILE_CONFIRM_EMPHASIS;
    }

    const confirmPanelClassName = [
      styles.confirmPanel,
      locale === 'en' && mode === 'desktop'
        ? styles.desktopFinalConfirmPanel
        : '',
      isEnglishMobileFlow ? styles.mobileFinalConfirmPanelEn : '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <section className={confirmPanelClassName}>
        {text.confirmTitle && !isEnglishMobileFlow ? (
          <h2 className={styles.confirmTitle}>{text.confirmTitle}</h2>
        ) : null}
        <p className={styles.confirmHint}>{confirmHint}</p>
        {confirmEmphasis ? (
          <p className={styles.confirmEmphasis}>{confirmEmphasis}</p>
        ) : null}
      </section>
    );
  };

  const renderCurrentPanel = () => {
    if (current === 0) return renderRisk();
    if (current === 1) return renderVerification();
    return renderConfirm();
  };

  const renderRiskAgreeText = () => {
    if (isEnglishMobileFlow) {
      return EN_MOBILE_RISK_AGREE;
    }

    if (mode !== 'mobile' || locale !== 'zh') {
      return text.riskAgree;
    }

    return (
      <span className={styles.riskAgreeText}>
        我已阅读并同意“
        <span className={styles.riskAgreeLink}>账号注销风险需知</span>”
      </span>
    );
  };

  const renderFooter = () => (
    <footer
      className={
        mode === 'mobile'
          ? `${styles.mobileFooter} ${
              isEnglishMobileFlow ? styles.mobileFooterEn : ''
            }`.trim()
          : `${styles.footer} ${current > 0 ? styles.footerEndOnly : ''}`
      }
    >
      {current === 0 ? (
        <Tooltip
          content={text.riskReadTip}
          disabled={riskReadComplete}
          destroyOnClose
          showArrow
        >
          <span className={styles.riskAgreeGuard}>
            <Checkbox
              checked={riskAccepted}
              disabled={!riskReadComplete}
              onChange={(checked) => {
                if (!riskReadComplete) {
                  return;
                }
                setRiskAccepted(checked);
              }}
            >
              {renderRiskAgreeText()}
            </Checkbox>
          </span>
        </Tooltip>
      ) : null}
      <div className={styles.footerActions}>
        {mode === 'mobile' && current === STEP_COUNT - 1 ? (
          <Button
            className={styles.mobileSubmitButton}
            size="large"
            theme="primary"
            loading={submitting}
            onClick={submitDeletion}
          >
            {isEnglishMobileFlow ? EN_FINAL_SUBMIT : text.submit}
          </Button>
        ) : null}
        {mode === 'mobile' && current === STEP_COUNT - 1 ? (
          <Button
            className={styles.mobileCancelButton}
            size="large"
            theme="primary"
            onClick={onClose}
          >
            {text.cancel}
          </Button>
        ) : null}
        {mode === 'desktop' && current === STEP_COUNT - 1 ? (
          <Button
            className={`${styles.footerCancelButton} ${
              locale === 'en' ? styles.footerCancelButtonFinalEn : ''
            }`.trim()}
            size="large"
            theme="default"
            variant="base"
            onClick={onClose}
          >
            {text.cancel}
          </Button>
        ) : null}
        {mode !== 'mobile' || current !== STEP_COUNT - 1 ? (
          <Button
            className={`${styles.footerNextButton} ${
              locale === 'en' && mode === 'desktop' && current < STEP_COUNT - 1
                ? styles.footerNextButtonExact
                : ''
            } ${
              locale === 'en' &&
              mode === 'desktop' &&
              current === STEP_COUNT - 1
                ? styles.footerConfirmButtonFinalEn
                : ''
            }`.trim()}
            size="large"
            theme="primary"
            disabled={!canNext || verifyingCode}
            loading={submitting || verifyingCode}
            onClick={goNext}
          >
            {locale === 'en' && current === STEP_COUNT - 1
              ? EN_FINAL_SUBMIT
              : current === STEP_COUNT - 1
              ? text.submit
              : text.next}
          </Button>
        ) : null}
      </div>
    </footer>
  );

  const renderHeader = () => (
    <div className={styles.dialogHeader}>
      <InfoCircleFilledIcon className={styles.dialogHeaderIcon} size={24} />
      <span className={styles.dialogHeaderTitle}>{text.title}</span>
      <button
        type="button"
        className={styles.dialogCloseButton}
        aria-label="close"
        onClick={onClose}
      >
        <CloseIcon size={16} />
      </button>
    </div>
  );

  if (mode === 'mobile') {
    if (desktopStage === 'intro') {
      return renderMobileIntroDialog();
    }

    return (
      <div
        className={`${styles.mobilePage} ${
          isEnglishMobileFlow ? styles.mobilePageEn : ''
        }`.trim()}
      >
        <header className={styles.mobileHeader}>
          <button
            type="button"
            className={styles.mobileBackButton}
            aria-label="back"
            onClick={goPrevious}
          >
            <ChevronLeftIcon size={24} />
          </button>
          <span>{locale === 'zh' ? '注销账号' : text.title}</span>
        </header>
        <main
          ref={mobileScrollRef}
          className={styles.mobileScroll}
          onScroll={checkRiskReadComplete}
        >
          <div className={styles.mobileCard}>
            {renderSteps(styles.mobileSteps)}
            {renderCurrentPanel()}
          </div>
        </main>
        {renderFooter()}
      </div>
    );
  }

  if (desktopStage === 'intro') {
    return renderIntroDialog();
  }

  return (
    <Dialog
      className={styles.desktopDialog}
      dialogClassName={styles.desktopDialogCard}
      visible={open}
      width={1200}
      placement="center"
      closeBtn={false}
      closeOnEscKeydown={false}
      closeOnOverlayClick={false}
      footer={false}
      header={renderHeader()}
      onClose={onClose}
    >
      <div className={styles.dialogShell}>
        <main className={styles.desktopBody}>
          {renderSteps(styles.steps)}
          {renderCurrentPanel()}
        </main>
        {renderFooter()}
      </div>
    </Dialog>
  );
};

export const AccountDeletionDangerEntry = ({
  locale = 'zh',
  onClick,
}: {
  locale?: AccountDeletionLocale;
  onClick: () => void;
}) => (
  <div className={styles.dangerEntry}>
    <Button
      type="button"
      theme="default"
      variant="outline"
      className={styles.dangerEntryButton}
      icon={<PoweroffIcon size={16} />}
      onClick={onClick}
    >
      {locale === 'en' ? 'Delete Account' : '账号注销'}
    </Button>
  </div>
);
